import React, { useEffect, useMemo, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import IPInput from '../../../../../components/IPInput/IPInput'
import IPButton from '../../../../../components/IPButton/IPButton'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'

import { IDealerInformationVDF } from '../models/model'
import { GetDealerInformationListApi } from '../services/getDealerInformationListApi'
import { AddDealerInformationApi } from '../services/addDealerInformationApi'
import { AddDealerInformationListApi } from '../services/addDealerInformationListApi'
import { UpdateDealerInformationApi } from '../services/updateDealerInformationListApi'

type EditableDealerRow = IDealerInformationVDF & {
  _isDirty?: boolean
}

function DealerInformationVDFList(props: BaseComponentWithContextProps) {
  const [loading, setLoading] = useState(false)
  const [savingAll, setSavingAll] = useState(false)
  const [excelUploading, setExcelUploading] = useState(false)

  const [rows, setRows] = useState<EditableDealerRow[]>([])
  const [newRow, setNewRow] = useState<Partial<IDealerInformationVDF> | null>(null)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const dirtyCount = useMemo(() => rows.filter(r => r._isDirty).length, [rows])

  const showError = (title: string, err: any) => {
    const msg =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      err?.description ||
      err ||
      'Beklenmeyen bir hata oluştu.'
    Swal.fire({ icon: 'error', title, text: String(msg) })
  }

  const fetchList = async () => {
    setLoading(true)
    try {
      const api = new GetDealerInformationListApi({})
      const data = await api.getDealerInformationList()
      const list: IDealerInformationVDF[] = Array.isArray(data) ? data : (data as any)?.data ?? []

      setRows(
        (list || []).map(x => ({
          ...x,
          _isDirty: false,
        }))
      )
    } catch (err) {
      showError('Liste getirilemedi', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChangeRow = (index: number, field: keyof IDealerInformationVDF, value: any) => {
    setRows(prev => {
      const copy = [...prev]
      const current = { ...copy[index] }
        ; (current as any)[field] = value
      current._isDirty = true
      copy[index] = current
      return copy
    })
  }

  const onToggleDeleted = (index: number, checked: boolean) => {
    setRows(prev => {
      const copy = [...prev]
      const current = { ...copy[index] }
      current.Deleted = checked
      current._isDirty = true
      copy[index] = current
      return copy
    })
  }

  const onSaveAll = async () => {
    const dirtyRows = rows.filter(r => r._isDirty)

    if (dirtyRows.length === 0) {
      Swal.fire({ icon: 'info', title: 'Kayıt yok', text: 'Güncellenecek (değişmiş) kayıt bulunamadı.' })
      return
    }

    const payload = dirtyRows.map(r => ({
      Id: r.Id,
      DealerCode: (r.DealerCode || '').trim(),
      PhoneNumber: r.PhoneNumber ?? '',
      NameSurname: r.NameSurname ?? '',
      Deleted: !!r.Deleted,
    }))

    const emptyDealerCodes = payload.filter(p => !p.DealerCode).length
    if (emptyDealerCodes > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Eksik bilgi',
        text: 'DealerCode boş olan (değişmiş) satırlar var. Lütfen bayi kodlarını doldurun.',
      })
      return
    }

    setSavingAll(true)
    try {
      const api = new UpdateDealerInformationApi({})
      const res = await api.updateDealerInformationList(payload as any)

      const message = (res as any)?.data ?? (res as any) ?? 'Güncelleme tamamlandı.'
      Swal.fire({ icon: 'success', title: 'Başarılı', text: message })

      await fetchList()
    } catch (err) {
      showError('Güncelleme başarısız', err)
    } finally {
      setSavingAll(false)
    }
  }

  const onAddNewRow = () => {
    if (newRow) return
    setNewRow({
      DealerCode: '',
      PhoneNumber: '',
      NameSurname: '',
      Deleted: false,
    })
  }

  const onCancelNewRow = () => setNewRow(null)

  const onSaveNewRow = async () => {
    if (!newRow) return

    const dealerCode = (newRow.DealerCode || '').toString().trim()
    if (!dealerCode) {
      Swal.fire({ icon: 'warning', title: 'Eksik bilgi', text: 'Bayi kodu (DealerCode) zorunludur.' })
      return
    }

    const payload = {
      DealerCode: dealerCode,
      PhoneNumber: (newRow.PhoneNumber || '').toString(),
      NameSurname: (newRow.NameSurname || '').toString(),
    }

    try {
      const api = new AddDealerInformationApi({})
      const res = await api.addDealerInformation(payload as any)

      const isOk = (res as any)?.data === true || res === true
      if (isOk) {
        Swal.fire({ icon: 'success', title: 'Başarılı', text: 'Kayıt eklendi.' })
      } else {
        const maybeError = (res as any)?.error
        const maybeMsg = (res as any)?.data ?? (res as any)
        if (maybeError) Swal.fire({ icon: 'warning', title: 'Uyarı', text: maybeError })
        else Swal.fire({ icon: 'success', title: 'Sonuç', text: maybeMsg ?? 'İşlem tamamlandı.' })
      }

      setNewRow(null)
      await fetchList()
    } catch (err) {
      showError('Ekleme başarısız', err)
    }
  }

  const onClickExcelUpload = () => fileInputRef.current?.click()

  const normalizeHeader = (h: string) =>
    (h || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/ı/g, 'i')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')

  const mapHeaders = (headers: string[]) => {
    const idx = (pred: (h: string) => boolean) => headers.findIndex(h => pred(normalizeHeader(h)))
    const dealerCodeIndex = idx(h => h === 'dealercode' || h === 'bayikodu' || h === 'bayikod' || h === 'dealer' || h === 'bayi')
    const phoneNumberIndex = idx(h => h === 'phonenumber' || h === 'telefon' || h === 'telefonno' || h === 'telefonnumarasi' || h === 'gsm')
    const nameSurnameIndex = idx(h => h === 'namesurname' || h === 'adsoyad' || h === 'adisoyadi' || h === 'adisoyad' || h === 'yetkili')
    return { dealerCodeIndex, phoneNumberIndex, nameSurnameIndex }
  }

  const parseExcelFile = async (
    file: File
  ): Promise<Array<Pick<IDealerInformationVDF, 'DealerCode' | 'NameSurname' | 'PhoneNumber'>>> => {
    const XLSX = await import('xlsx')

    const data = await file.arrayBuffer()
    const wb = XLSX.read(data, { type: 'array' })
    const firstSheetName = wb.SheetNames?.[0]
    if (!firstSheetName) return []

    const ws = wb.Sheets[firstSheetName]
    const json = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, raw: false }) as any[][]
    if (!json || json.length < 2) return []

    const headers = (json[0] || []).map(h => (h ?? '').toString())
    const { dealerCodeIndex, phoneNumberIndex, nameSurnameIndex } = mapHeaders(headers)

    // varsayılan sıralama: DealerCode, PhoneNumber, NameSurname
    const dcIdx = dealerCodeIndex >= 0 ? dealerCodeIndex : 0
    const pnIdx = phoneNumberIndex >= 0 ? phoneNumberIndex : 1
    const nsIdx = nameSurnameIndex >= 0 ? nameSurnameIndex : 2

    const out: Array<Pick<IDealerInformationVDF, 'DealerCode' | 'NameSurname' | 'PhoneNumber'>> = []

    for (let i = 1; i < json.length; i++) {
      const row = json[i]
      if (!row || row.length === 0) continue

      const dealerCode = (row[dcIdx] ?? '').toString().trim()
      if (!dealerCode) continue

      out.push({
        DealerCode: dealerCode,
        PhoneNumber: (row[pnIdx] ?? '').toString().trim(),
        NameSurname: (row[nsIdx] ?? '').toString().trim(),
      })
    }

    return out
  }

  const onExcelSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    setExcelUploading(true)
    try {
      const list = await parseExcelFile(file)

      if (!list || list.length === 0) {
        Swal.fire({
          icon: 'info',
          title: 'Dosya boş',
          text: 'Dosyadan okunabilir kayıt bulunamadı. Başlık satırı ve en az 1 veri satırı olduğundan emin olun.',
        })
        return
      }

      const api = new AddDealerInformationListApi({})
      const res = await api.addDealerInformationList(list as any)

      const message = (res as any)?.data ?? (res as any) ?? 'Excel yükleme tamamlandı.'
      Swal.fire({ icon: 'success', title: 'Sonuç', text: message })

      await fetchList()
    } catch (err) {
      showError('Excel yükleme başarısız', err)
    } finally {
      setExcelUploading(false)
    }
  }

  return (
    <div className="">
      <div className="content col-lg-12 m-auto">
        <div className="card">
          <div className="card-header d-flex align-items-center">
            <span className="h4 mb-0">Bayi İletişim Bilgileri</span>

            <div className="d-flex gap-2 ms-auto me-3">
              <div className='buttonsuz' style={{ marginRight: '10px' }}>
                <a className='campaignExcelBtn' href='/assetWeb/docs/dealerInfoVDF.xlsx' target='_blank'>
                  Örnek Excel
                </a>
              </div>

              <IPButton
                text={excelUploading ? 'Yükleniyor...' : 'Excel Yükle'}
                disabled={loading || savingAll || excelUploading}
                onClick={onClickExcelUpload}
              />

              <IPButton text={loading ? 'Yükleniyor...' : 'Yenile'} disabled={loading || savingAll || excelUploading} onClick={fetchList} />

              <IPButton text="Ekle" disabled={loading || savingAll || excelUploading || !!newRow} onClick={onAddNewRow} />

              <IPButton
                text={savingAll ? 'Kaydediliyor...' : `Değişiklikleri Kaydet ${dirtyCount > 0 ? ` - ${dirtyCount}` : ''}`}
                disabled={loading || savingAll || excelUploading || dirtyCount === 0}
                onClick={onSaveAll}
              />
            </div>

            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={onExcelSelected} />
          </div>
          {rows?.length > 0 && (
            <div className="card-body">
              <b>Toplam Veri Sayısı: {rows.length}</b>
            </div>
          )
          }
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">Liste yükleniyor...</div>
            ) : (
              <>
                {newRow && (
                  <div className="mb-3 p-3 border rounded">
                    <div className="row g-2 align-items-end">
                      <div className="col-md-2">
                        <label className="form-label">Bayi Kodu</label>
                        <IPInput
                          value={(newRow.DealerCode ?? '') as any}
                          onChange={(e: any) => setNewRow(prev => ({ ...(prev || {}), DealerCode: e?.target?.value ?? e }))}
                          type={'text'}
                        />
                      </div>

                      <div className="col-md-2">
                        <label className="form-label">Telefon</label>
                        <IPInput
                          value={(newRow.PhoneNumber ?? '') as any}
                          onChange={(e: any) => setNewRow(prev => ({ ...(prev || {}), PhoneNumber: e?.target?.value ?? e }))}
                          type={'text'}
                        />
                      </div>

                      <div className="col-md-3">
                        <label className="form-label">Ad Soyad</label>
                        <IPInput
                          value={(newRow.NameSurname ?? '') as any}
                          onChange={(e: any) => setNewRow(prev => ({ ...(prev || {}), NameSurname: e?.target?.value ?? e }))}
                          type={'text'}
                        />
                      </div>

                      <div className="col-md-2 d-flex gap-2">
                        <IPButton text="Kaydet" onClick={onSaveNewRow} />
                        <IPButton text="İptal" onClick={onCancelNewRow} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="table-responsive">
                  <table className="table table-striped table-hover align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: 50 }} className="text-center">
                          Sil
                        </th>
                        <th style={{ width: 150 }}>Bayi Kodu</th>
                        <th style={{ width: 150 }}>Telefon</th>
                        <th style={{ width: 220 }}>Ad Soyad</th>
                        <th style={{ width: 120 }}>Durum</th>
                      </tr>
                    </thead>

                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center py-4">
                            Kayıt bulunamadı.
                          </td>
                        </tr>
                      ) : (
                        rows.map((r, i) => (
                          <tr key={r.Id ?? `${r.DealerCode}-${i}`} className={r.Deleted ? 'table-danger' : ''}>
                            <td className="text-center">
                              <input type="checkbox" checked={!!r.Deleted} onChange={e => onToggleDeleted(i, e.target.checked)} />
                            </td>

                            <td>
                              <IPInput
                                value={r.DealerCode?.replace('0000', '') as any}
                                onChange={(e: any) => onChangeRow(i, 'DealerCode', e?.target?.value ?? e)}
                                type={'text'}
                              />
                            </td>

                            <td>
                              <IPInput
                                value={r.PhoneNumber as any}
                                onChange={(e: any) => onChangeRow(i, 'PhoneNumber', e?.target?.value ?? e)}
                                type={'text'}
                              />
                            </td>

                            <td>
                              <IPInput
                                value={r.NameSurname as any}
                                onChange={(e: any) => onChangeRow(i, 'NameSurname', e?.target?.value ?? e)}
                                type={'text'}
                              />
                            </td>

                            <td>
                              {r.Deleted ? (
                                <span className="badge bg-danger">Silinecek</span>
                              ) : r._isDirty ? (
                                <span className="badge bg-warning text-dark">Değiştirildi</span>
                              ) : (
                                <span className="badge bg-secondary">Değişmedi</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default componentWithContext(DealerInformationVDFList)
